import { useCallback, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Box,
  Gap,
  ISelectMiniOption,
  SearchBox,
  SelectMini,
  StatusChip,
  StatusChipType,
  Table,
} from '@salecycle/ui-component-library';
import { StandardMainArea } from 'components/experimental/StandardMainArea';

import { useFeatureFlags } from '../../../../core/app/AppContext';
import { useGetAllOnsiteCampaignsQuery } from '../../hooks/useGetAllOnsiteCampaignsQuery';
import { deriveStatus } from './utils/status';
import { filterCampaignsByStatus, filterCampaignsByName } from './utils/filters';
import { AgentChat } from '@salecycle/experimental/Agent';
import { FloatingButton } from '@salecycle/experimental/Agent/FloatingButton';
import { OnsiteCampaign } from 'sections/onsite-campaigns/context/OnsiteCampaignSectionState';
import { useOnsiteCampaignsCache } from 'sections/onsite-campaigns/hooks/useOnsiteCampaignsCache';
import { AgentFeature, AgentQueryResponse } from '@salecycle/experimental/Agent/types';

const OnsiteActionsBar = styled.div`
  padding-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export function CampaignList() {
  const { t } = useTranslation('campaigns');
  const history = useHistory();
  const { siteId } = useParams<{ siteId: string }>();
  const { flagEnabled } = useFeatureFlags();

  const urlParams = new URLSearchParams(window.location.search);
  const selectedStatus = urlParams.get('status') ?? 'All';
  const selectedName = urlParams.get('name') ?? '';
  const [statusFilter, setStatusFilter] = useState<string>(selectedStatus);
  const [nameFilter, setNameFilter] = useState<string>(selectedName);
  const [isAgentOpen, setIsAgentOpen] = useState(false);

  const statuses: Array<keyof StatusChipType> = ['Draft', 'Scheduled', 'Test', 'Live', 'Finished'];

  const statusFilters = [{ value: 'All', label: 'All' }];
  statuses.forEach(status => {
    statusFilters.push({ value: status, label: status });
  });

  const campaignsQuery = useGetAllOnsiteCampaignsQuery(siteId);
  const { addCampaignToCache } = useOnsiteCampaignsCache(siteId);
  const [campaignsInView, setCampaignsInView] = useState<OnsiteCampaign[]>([]);
  const showPopulatedView = !campaignsQuery.isLoading && !!campaignsInView.length;

  useEffect(() => {
    setCampaignsInView(filterCampaignsByName(filterCampaignsByStatus(campaignsQuery.data, statusFilter), nameFilter));
  }, [campaignsQuery.data, nameFilter, statusFilter]);

  const updateCampaignList = useCallback(
    (data: AgentQueryResponse['data']) => data?.campaign?.id && addCampaignToCache(data.campaign),
    [addCampaignToCache],
  );

  const toggleAgent = useCallback(() => setIsAgentOpen(prev => !prev), []);

  const columns = [
    {
      Header: 'Campaign Name',
      accessor: 'name',
      Cell: props => (
        <Link to={`/sites/${props.original.siteId}/campaigns/onsite/${props.original.id}/workflow`}>{props.value}</Link>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: props => {
        const status = deriveStatus(props.original.startDate, props.original.endDate, props.original.status);
        return <StatusChip aria-label={'Campaign Status'} label={status} variation={status} />;
      },
    },
  ];

  if (flagEnabled('ld-enable-onsite-scheduling')) {
    columns.push(
      {
        Header: 'Start Date',
        accessor: 'startDate',
        Cell: props => (
          <>
            {props.value
              ? `${moment(props.value).format('DD/MM/YYYY HH:mm')} UTC`
              : t('onsite.editor.workflow.scheduling.startDatePlaceholder')}
          </>
        ),
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        Cell: props => (
          <>
            {props.value
              ? `${moment(props.value).format('DD/MM/YYYY HH:mm')} UTC`
              : t('onsite.editor.workflow.scheduling.endDatePlaceholder')}
          </>
        ),
      },
    );
  }

  return (
    <>
      <StandardMainArea
        isLoading={campaignsQuery.isLoading}
        isError={campaignsQuery.isError}
        heading={t('onsite.heading')}
        errorHeading={t('onsite.errorLoadingCampaigns')}
        headingButtonText={t('onsite.createCampaignButton.label')}
        onHeadingButtonClicked={() => {
          history.push(`/sites/${siteId}/campaigns/onsite/new/workflow`);
        }}
        isAgentOpen={isAgentOpen}
        agentComponent={
          flagEnabled('ld-campy-assistant') && (
            <AgentChat
              siteId={siteId}
              feature={AgentFeature.Onsite}
              filters={{
                status: statusFilter !== 'All' ? statusFilter : undefined,
                name: nameFilter || undefined,
              }}
              onClose={toggleAgent}
              onResponse={updateCampaignList}
            />
          )
        }
      >
        <OnsiteActionsBar>
          {flagEnabled('ld-enable-onsite-scheduling') && (
            <>
              <SelectMini
                label={'Status:'}
                ariaLabel={'Status'}
                options={statusFilters}
                menuWidth="auto"
                onChange={(option: ISelectMiniOption) => setStatusFilter(option.value)}
                placeholder={selectedStatus}
              />
            </>
          )}
          <SearchBox
            value={nameFilter}
            onChange={setNameFilter}
            width={310}
            size="campaign-list"
            variation="secondary"
            placeholder="Search campaigns..."
            name="campaign-search"
          />
        </OnsiteActionsBar>

        {showPopulatedView && campaignsInView.length ? (
          <Table data={campaignsInView} columns={columns} />
        ) : (
          <Box stackItems={true} horizontalPositioning="center" padding="40px 0">
            <h3>{t('noCampaigns')}</h3>
          </Box>
        )}
        <Gap bottom={120} />
      </StandardMainArea>
      {flagEnabled('ld-campy-assistant') && !isAgentOpen && (
        <FloatingButton onClick={toggleAgent} data-testid="floating-button">
          <img src="/images/campy_small.png" alt={t('campaignAssistant.agentName')} width={60} height={60} />
        </FloatingButton>
      )}
    </>
  );
}
